import React, { useState, useEffect } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from "date-fns";
import { DateRange } from "@/services/aiDataFetchService";
import { Loader2, Calendar, AlertCircle, CheckCircle, ChevronDown, ChevronRight, Copy, Code, Settings, FileText, Edit, ExternalLink, Globe } from "lucide-react";
import { toast } from "sonner";
import { SystemPromptEditModal } from "./SystemPromptEditModal";
import { Label } from "@/components/ui/label";

interface SessionDatesFetchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateSessions: (selectedRanges: DateRange[]) => Promise<void>;
  dateRanges: DateRange[];
  programName: string;
  dataFetchUrl: string;
  isLoading?: boolean;
  isFetching?: boolean;
  fetchError?: string;
  debugInfo?: {
    processedHtmlContent: string;
    originalBodyContent: string;
    savedPatterns: string[];
    processingMethod: string;
    explanation: string;
    systemPrompt?: string;
    aiResponse?: string;
    systemPromptTemplate?: string;
  };
  urlResults?: Array<{
    url: string;
    success: boolean;
    rangesFound: number;
    error?: string;
  }>;
  locationId?: string;
  programKey?: string;
  onRefetch?: () => void;
}

interface DateRangeWithSelection extends DateRange {
  id: string;
  selected: boolean;
}

// Helper function to truncate URLs in the middle
const truncateUrlMiddle = (url: string, startChars: number = 15, endChars: number = 10): string => {
  if (url.length <= startChars + endChars + 3) {
    return url;
  }
  return `${url.substring(0, startChars)}...${url.substring(url.length - endChars)}`;
};

export const SessionDatesFetchModal: React.FC<SessionDatesFetchModalProps> = ({
  isOpen,
  onClose,
  onCreateSessions,
  dateRanges,
  programName,
  dataFetchUrl,
  isLoading = false,
  isFetching = false,
  fetchError,
  debugInfo,
  urlResults,
  locationId,
  programKey,
  onRefetch
}) => {
  const [selectableRanges, setSelectableRanges] = useState<DateRangeWithSelection[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [isTechnicalDetailsOpen, setIsTechnicalDetailsOpen] = useState(true);
  const [isSystemPromptModalOpen, setIsSystemPromptModalOpen] = useState(false);
  const [isUrlListOpen, setIsUrlListOpen] = useState(false); // New state for collapsible URL list

  // New state for pre-loading wrapper patterns
  const [wrapperPatterns, setWrapperPatterns] = useState<string[]>([]);
  const [loadingPatterns, setLoadingPatterns] = useState(false);
  const [patternsError, setPatternsError] = useState<string | null>(null);

  // Enhanced logging when modal opens or data changes
  React.useEffect(() => {
    if (isOpen) {
      console.log(`🔍 [SessionDatesFetchModal] Modal opened with data:`, {
        isFetching,
        fetchError,
        dateRangesCount: dateRanges.length,
        hasDebugInfo: !!debugInfo,
        hasUrlResults: !!urlResults,
        urlResultsCount: urlResults?.length || 0
      });
      
      if (fetchError) {
        console.error(`❌ [SessionDatesFetchModal] Error state:`, fetchError);
      }
      
      if (debugInfo) {
        console.log(`🔧 [SessionDatesFetchModal] Debug info available:`, {
          processingMethod: debugInfo.processingMethod,
          explanation: debugInfo.explanation,
          htmlContentLength: debugInfo.processedHtmlContent?.length || 0,
          originalContentLength: debugInfo.originalBodyContent?.length || 0,
          patternsCount: debugInfo.savedPatterns?.length || 0,
          hasSystemPrompt: !!debugInfo.systemPrompt,
          hasAiResponse: !!debugInfo.aiResponse
        });
      }
      
      if (urlResults && urlResults.length > 0) {
        console.log(`🌐 [SessionDatesFetchModal] URL Results:`, 
          urlResults.map((result, index) => ({
            index: index + 1,
            url: result.url,
            success: result.success,
            rangesFound: result.rangesFound,
            error: result.error
          }))
        );
      }
    }
  }, [isOpen, isFetching, fetchError, dateRanges, debugInfo, urlResults]);

  // Initialize selectable ranges when dateRanges change
  useEffect(() => {
    const ranges = dateRanges.map((range, index) => ({
      ...range,
      id: `range-${index}`,
      selected: true // All checkboxes checked by default
    }));
    setSelectableRanges(ranges);
    
    if (ranges.length > 0) {
      console.log(`📋 [SessionDatesFetchModal] Initialized ${ranges.length} selectable date ranges`);
    }
  }, [dateRanges]);

  // Load wrapper patterns when modal opens
  useEffect(() => {
    if (isOpen && locationId) {
      loadWrapperPatterns();
    }
  }, [isOpen, locationId]);

  const loadWrapperPatterns = async () => {
    if (!locationId) return;
    
    setLoadingPatterns(true);
    setPatternsError(null);
    
    try {
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('@/services/firebaseService');
      
      const configDocRef = doc(db, 'campLocations', locationId, 'fetch', '_config');
      const configDoc = await getDoc(configDocRef);
      
      if (!configDoc.exists()) {
        console.log('No patterns config found for location:', locationId);
        setWrapperPatterns([]);
        return;
      }
      
      const data = configDoc.data();
      const patterns = data?.contentExtractionPatterns || [];
      
      const patternStrings = patterns.map((p: any) => p.pattern);
      console.log('Loaded wrapper patterns:', patternStrings);
      
      setWrapperPatterns(patternStrings);
    } catch (error) {
      console.error('Error loading wrapper patterns:', error);
      setPatternsError(error instanceof Error ? error.message : 'Unknown error');
      setWrapperPatterns([]);
    } finally {
      setLoadingPatterns(false);
    }
  };

  const handleToggleAll = (checked: boolean) => {
    setSelectableRanges(prev => 
      prev.map(range => ({ ...range, selected: checked }))
    );
  };

  const handleToggleRange = (id: string) => {
    setSelectableRanges(prev =>
      prev.map(range =>
        range.id === id ? { ...range, selected: !range.selected } : range
      )
    );
  };

  const selectedCount = selectableRanges.filter(range => range.selected).length;
  const allSelected = selectableRanges.length > 0 && selectedCount === selectableRanges.length;
  const someSelected = selectedCount > 0 && selectedCount < selectableRanges.length;

  const handleCreateSessions = async () => {
    const selectedRanges = selectableRanges
      .filter(range => range.selected)
      .map(({ id, selected, ...range }) => range);

    if (selectedRanges.length === 0) {
      return;
    }

    try {
      setIsCreating(true);
      await onCreateSessions(selectedRanges);
    } catch (error) {
      console.error("Error creating sessions:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleEditSystemPrompt = () => {
    if (!locationId || !programKey) {
      toast.error("Missing location or program information");
      return;
    }
    setIsSystemPromptModalOpen(true);
  };

  const handleSystemPromptUpdated = () => {
    toast.success("System prompt updated successfully");
    if (onRefetch) {
      toast.info("Re-fetch session dates to use the updated prompt");
    }
  };

  const handleCopyProcessedHtml = async () => {
    if (!debugInfo?.processedHtmlContent) return;
    
    try {
      await navigator.clipboard.writeText(debugInfo.processedHtmlContent);
      toast.success("Processed HTML copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleCopyOriginalHtml = async () => {
    if (!debugInfo?.originalBodyContent) {
      toast.error("No original HTML content available");
      return;
    }
    
    try {
      await navigator.clipboard.writeText(debugInfo.originalBodyContent);
      toast.success("Original HTML content copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy original HTML content");
    }
  };

  const handleCopyAIResponse = async () => {
    if (!debugInfo?.aiResponse) return;
    
    try {
      await navigator.clipboard.writeText(debugInfo.aiResponse);
      toast.success("AI response copied to clipboard");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      toast.error("Failed to copy to clipboard");
    }
  };

  const modalTitle = isFetching 
    ? (urlResults && urlResults.length > 1 
       ? `Processing ${urlResults.filter(r => r.error !== 'Pending...' && r.error !== 'Processing...').length}/${urlResults.length} URLs...`
       : "Fetching session dates...") 
    : fetchError 
    ? "Failed to fetch session dates"
    : `Session dates for ${programName}`;

  const modalDescription = (
    <div className="space-y-2">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-slate-700">
            <strong>Program:</strong> {programName}
          </p>
          <div className="text-slate-600 text-sm">
            <strong>Source URL{urlResults && urlResults.length > 1 ? 's' : ''}:</strong> 
            {urlResults && urlResults.length > 1 ? (
              <div className="mt-1">
                <Collapsible open={isUrlListOpen} onOpenChange={setIsUrlListOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-auto p-0 font-normal text-slate-600 hover:text-slate-800">
                      <span className="flex items-center gap-1">
                        {isUrlListOpen ? (
                          <ChevronDown className="w-3 h-3" />
                        ) : (
                          <ChevronRight className="w-3 h-3" />
                        )}
                        <span className="text-xs">
                          {urlResults.length} URLs ({urlResults.filter(r => r.success).length} successful, {urlResults.filter(r => !r.success && r.error !== 'Pending...' && r.error !== 'Processing...').length} failed)
                        </span>
                      </span>
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-2">
                    <div className="space-y-1 max-h-40 overflow-y-auto bg-slate-50 rounded p-2">
                      {urlResults.map((result, index) => (
                        <div key={index} className="flex items-center gap-1 text-xs">
                          {result.success ? (
                            <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                          ) : result.error === 'Processing...' ? (
                            <Loader2 className="w-3 h-3 text-blue-600 animate-spin flex-shrink-0" />
                          ) : result.error === 'Pending...' ? (
                            <div className="w-3 h-3 rounded-full border border-gray-300 flex-shrink-0" />
                          ) : (
                            <AlertCircle className="w-3 h-3 text-red-600 flex-shrink-0" />
                          )}
                          <span className="font-mono text-xs" title={result.url}>
                            {truncateUrlMiddle(result.url, 25, 15)}
                          </span>
                          <span className="text-slate-500 text-xs">
                            {result.error === 'Processing...' ? 'Processing...' :
                             result.error === 'Pending...' ? 'Pending' :
                             result.success ? `${result.rangesFound} sessions` : 
                             result.error}
                          </span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            ) : (
              <span className="font-mono text-xs ml-1">{truncateUrlMiddle(dataFetchUrl, 30, 20)}</span>
            )}
          </div>
          {!isFetching && !fetchError && dateRanges.length > 0 && (
            <p className="text-slate-600 text-sm">
              Select the date ranges you want to create sessions for. All dates are pre-selected.
            </p>
          )}
          {isFetching && urlResults && urlResults.length > 1 && (
            <p className="text-blue-600 text-sm mt-2">
              <Loader2 className="w-4 h-4 inline animate-spin mr-2" />
              Processing URLs individually for real-time progress updates...
            </p>
          )}
        </div>
        {locationId && programKey && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleEditSystemPrompt}
            className="flex items-center gap-2 ml-4"
          >
            <Edit className="w-4 h-4" />
            Edit AI prompt
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <>
      <BigFormModal
        isOpen={isOpen}
        onClose={onClose}
        title={modalTitle}
        description={modalDescription}
        onSave={handleCreateSessions}
        saveLabel={`Create ${selectedCount} new session${selectedCount !== 1 ? 's' : ''}`}
        isSaving={isCreating}
        hideActions={isFetching || !!fetchError || dateRanges.length === 0}
      >
        <div className="space-y-4">
          {isFetching && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                <span className="text-lg">Analyzing webpage for session dates...</span>
              </div>
            </div>
          )}

          {/* Processing Status */}
          {isFetching && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-blue-600" />
                <div>
                  <div className="font-medium text-blue-900">
                    {urlResults ? `Processing ${urlResults.filter(r => r.success).length + 1}/${urlResults.length} URLs...` : 'Processing URLs...'}
                  </div>
                  <div className="text-sm text-blue-700">Fetching session dates from program data</div>
                </div>
              </div>
            </div>
          )}

          {/* Wrapper Patterns for Content Processing - MOVED TO TECHNICAL DETAILS SECTION */}

          {/* Error Display */}
          {fetchError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-red-800 mb-2">
                <AlertCircle className="w-5 h-5" />
                <span className="font-semibold">Session Fetch Failed</span>
              </div>
              <p className="text-red-700">{fetchError}</p>
              
              {/* Special guidance for SPA/JavaScript issues */}
              {fetchError.toLowerCase().includes('single page application') || 
               fetchError.toLowerCase().includes('javascript') ||
               fetchError.toLowerCase().includes('angular') ||
               fetchError.toLowerCase().includes('react') ||
               fetchError.toLowerCase().includes('minimal content') ||
               (debugInfo?.explanation?.toLowerCase().includes('angular') || 
                debugInfo?.explanation?.toLowerCase().includes('ng-')) ? (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                  <p className="text-sm font-semibold text-blue-800 mb-2">🤖 JavaScript-Rendered Content Detected</p>
                  <p className="text-sm text-blue-700 mb-2">
                    This page loads session data dynamically with JavaScript. Here are some solutions:
                  </p>
                  <ul className="text-sm text-blue-700 space-y-1 ml-4 list-disc">
                    <li><strong>Try API endpoints:</strong> Replace `/events/` with `/api/events/` in the URL</li>
                    <li><strong>Look for data feeds:</strong> Check for JSON or XML endpoints that provide raw data</li>
                    <li><strong>Contact Snapology:</strong> Ask for a server-rendered URL or data feed for this location</li>
                    <li><strong>Manual entry:</strong> Copy session dates manually from their website</li>
                    <li><strong>Alternative URLs:</strong> Try mobile versions or direct calendar feeds</li>
                  </ul>
                  <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded">
                    <p className="text-xs text-yellow-800">
                      💡 <strong>Tip:</strong> For Snapology locations, try contacting their support for a direct data feed URL.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                  <p className="text-sm text-yellow-800 mb-2">
                    Please check the URL and try again, or create sessions manually.
                  </p>
                  
                  {onRefetch && (
                    <Button 
                      onClick={onRefetch} 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 border-yellow-300 text-yellow-700 hover:bg-yellow-50"
                    >
                      Try Again
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {!isFetching && !fetchError && dateRanges.length === 0 && (
            <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <Calendar className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-medium text-yellow-800">No session dates found</h3>
                <p className="text-yellow-700 mt-1">
                  The AI algorithm couldn't find any recognizable date patterns on this webpage.
                </p>
                <p className="text-yellow-600 text-sm mt-2">
                  Try a different URL or create sessions manually.
                </p>
              </div>
            </div>
          )}

          {!isFetching && !fetchError && dateRanges.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">
                    Found {dateRanges.length} session date{dateRanges.length !== 1 ? 's' : ''}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleToggleAll(!allSelected)}
                >
                  {allSelected ? 'Unselect all' : 'Select all'}
                </Button>
              </div>

              {/* Horizontally scrollable table container */}
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12 sticky left-0 bg-white z-10 border-r">
                        <Checkbox
                          checked={allSelected}
                          ref={(el) => {
                            if (el) {
                              const checkbox = el.querySelector('input[type="checkbox"]') as HTMLInputElement;
                              if (checkbox) checkbox.indeterminate = someSelected;
                            }
                          }}
                          onCheckedChange={handleToggleAll}
                        />
                      </TableHead>
                      <TableHead className="min-w-[120px]">Start Date</TableHead>
                      <TableHead className="min-w-[120px]">End Date</TableHead>
                      <TableHead className="min-w-[100px]">Duration</TableHead>
                      <TableHead className="min-w-[100px]">Price</TableHead>
                      <TableHead className="min-w-[200px]">Title</TableHead>
                      <TableHead className="min-w-[200px]">URL</TableHead>
                      <TableHead className="min-w-[250px]">Description</TableHead>
                      <TableHead className="min-w-[200px]">Source URL</TableHead>
                      <TableHead className="min-w-[200px]">Original Text</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectableRanges.map((range) => (
                      <TableRow key={range.id}>
                        <TableCell className="sticky left-0 bg-white z-10 border-r">
                          <Checkbox
                            checked={range.selected}
                            onCheckedChange={() => handleToggleRange(range.id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium whitespace-nowrap">
                          {format(range.startDate, 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {format(range.endDate, 'MMM d, yyyy')}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <span className="text-slate-600">
                            {range.duration || `${range.numDays} day${range.numDays !== 1 ? 's' : ''}`}
                          </span>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {range.price ? (
                            <span className="font-medium text-green-600">{range.price}</span>
                          ) : (
                            <span className="text-slate-400 italic">Not found</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {range.displayTitle ? (
                            <span 
                              className="text-xs text-slate-700 block leading-relaxed"
                              title={range.displayTitle}
                            >
                              {range.displayTitle.length > 150 
                                ? `${range.displayTitle.substring(0, 150)}...` 
                                : range.displayTitle
                              }
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {range.url ? (
                            <span 
                              className="text-xs text-blue-600 underline block leading-relaxed break-all"
                              title={range.url}
                            >
                              {truncateUrlMiddle(range.url, 20, 15)}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[250px]">
                          {range.description ? (
                            <span 
                              className="text-xs text-slate-700 block leading-relaxed"
                              title={range.description}
                            >
                              {range.description.length > 150 
                                ? `${range.description.substring(0, 150)}...` 
                                : range.description
                              }
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {range.sourceUrl ? (
                            <span 
                              className="text-xs text-blue-600 underline block leading-relaxed break-all"
                              title={range.sourceUrl}
                            >
                              {truncateUrlMiddle(range.sourceUrl, 20, 15)}
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {range.originalText ? (
                            <span 
                              className="text-xs font-mono text-slate-500 block leading-relaxed"
                              title={range.originalText}
                            >
                              {range.originalText.length > 150 
                                ? `${range.originalText.substring(0, 150)}...` 
                                : range.originalText
                              }
                            </span>
                          ) : (
                            <span className="text-slate-400 italic text-xs">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Technical Details Section - Show when there's any debug info, error, or processing has occurred */}
          {(debugInfo || fetchError || (!isFetching && (dateRanges.length > 0 || urlResults))) && (
            <Collapsible open={isTechnicalDetailsOpen} onOpenChange={setIsTechnicalDetailsOpen}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full justify-between">
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Technical Details
                    {fetchError && <span className="text-red-600 text-sm ml-2">(Error details available)</span>}
                    {!fetchError && debugInfo && <span className="text-green-600 text-sm ml-2">(Debug info available)</span>}
                  </span>
                  {isTechnicalDetailsOpen ? 
                    <ChevronDown className="w-4 h-4" /> : 
                    <ChevronRight className="w-4 h-4" />
                  }
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                  
                  {/* Error Status Indicator */}
                  {fetchError && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-red-600" />
                        <span className="font-semibold text-red-800">Error Details</span>
                      </div>
                      <p className="text-red-700 text-sm mb-2">
                        <strong>Error:</strong> {fetchError}
                      </p>
                      <p className="text-red-600 text-xs">
                        Check the Technical Details below and browser console for more debugging information.
                      </p>
                    </div>
                  )}

                  {/* URL Results Summary */}
                  {urlResults && urlResults.length > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-800">URL Processing Results</span>
                      </div>
                      <div className="space-y-1">
                        {urlResults.map((result, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm">
                            {result.success ? (
                              <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                            )}
                            <span className="font-mono text-xs text-blue-700" title={result.url}>
                              URL {index + 1}: {truncateUrlMiddle(result.url, 25, 15)}
                            </span>
                            <span className="text-slate-600 text-xs">
                              {result.success ? `(${result.rangesFound} sessions)` : `(${result.error})`}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Processing Method */}
                  {debugInfo && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Code className="w-5 h-5 text-yellow-600" />
                        <span className="font-semibold text-yellow-800">Processing Method</span>
                      </div>
                      <p className="text-yellow-700 font-medium">{debugInfo.processingMethod}</p>
                      <p className="text-yellow-600 text-sm mt-1">{debugInfo.explanation}</p>
                    </div>
                  )}

                  {/* HTML Content Buttons */}
                  {debugInfo && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Button
                        onClick={handleCopyProcessedHtml}
                        variant="outline"
                        className="flex items-center gap-2 justify-start"
                        disabled={!debugInfo.processedHtmlContent}
                      >
                        <Copy className="w-4 h-4" />
                        Copy processed HTML
                      </Button>
                      <Button
                        onClick={handleCopyOriginalHtml}
                        variant="outline"
                        className="flex items-center gap-2 justify-start"
                        disabled={!debugInfo.originalBodyContent}
                      >
                        <FileText className="w-4 h-4" />
                        Copy original HTML
                      </Button>
                    </div>
                  )}

                  {/* Content Stats */}
                  {debugInfo && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="font-medium text-gray-700">Original HTML Length:</span>
                        <span className="ml-2 text-gray-600">{debugInfo.originalBodyContent?.length?.toLocaleString() || 0} characters</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Processed HTML Length:</span>
                        <span className="ml-2 text-gray-600">{debugInfo.processedHtmlContent?.length?.toLocaleString() || 0} characters</span>
                      </div>
                    </div>
                  )}

                  {/* Content Preview */}
                  {debugInfo && debugInfo.processedHtmlContent && (
                    <div className="space-y-3">
                      <h4 className="font-medium text-gray-700">Content Preview (First 500 characters)</h4>
                      <div className="bg-white border rounded-lg p-3">
                        <h5 className="text-sm font-medium text-gray-600 mb-2">Processed HTML:</h5>
                        <div className="text-xs font-mono text-gray-500 bg-gray-50 p-2 rounded max-h-32 overflow-y-auto">
                          {debugInfo.processedHtmlContent.substring(0, 500)}
                          {debugInfo.processedHtmlContent.length > 500 && '...'}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* System Prompt Details */}
                  {debugInfo?.systemPrompt && (
                    <div className="mt-4 space-y-3">
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label className="text-xs font-medium text-slate-600">
                            Resolved System Prompt (with real values)
                          </Label>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(debugInfo.systemPrompt);
                              toast.success("Resolved system prompt copied to clipboard");
                            }}
                            className="h-6 px-2 text-xs"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy resolved prompt
                          </Button>
                        </div>
                        <div className="bg-slate-50 p-3 rounded-md max-h-40 overflow-y-auto">
                          <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                            {debugInfo.systemPrompt}
                          </pre>
                        </div>
                      </div>

                      {debugInfo.systemPromptTemplate && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-medium text-slate-600">
                              System Prompt Template (with placeholders)
                            </Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                navigator.clipboard.writeText(debugInfo.systemPromptTemplate!);
                                toast.success("System prompt template copied to clipboard");
                              }}
                              className="h-6 px-2 text-xs"
                            >
                              <Copy className="w-3 h-3 mr-1" />
                              Copy template
                            </Button>
                          </div>
                          <div className="bg-slate-50 p-3 rounded-md max-h-40 overflow-y-auto">
                            <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                              {debugInfo.systemPromptTemplate}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* AI Response */}
                  {debugInfo?.aiResponse && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs font-medium text-slate-600">
                          AI Response
                        </Label>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleCopyAIResponse}
                          className="h-6 px-2 text-xs"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy AI response
                        </Button>
                      </div>
                      <div className="bg-slate-50 p-3 rounded-md max-h-40 overflow-y-auto">
                        <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                          {debugInfo.aiResponse}
                        </pre>
                      </div>
                    </div>
                  )}

                  {/* Fallback when no debug info but processing occurred */}
                  {!debugInfo && !fetchError && (!isFetching && (dateRanges.length > 0 || urlResults)) && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <span className="font-semibold text-amber-800">Limited Debug Information</span>
                      </div>
                      <p className="text-amber-700 text-sm">
                        Processing completed but detailed debug information is not available. Check the browser console for more details.
                      </p>
                    </div>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </BigFormModal>

      {/* System Prompt Edit Modal */}
      {locationId && programKey && (
        <SystemPromptEditModal
          isOpen={isSystemPromptModalOpen}
          onClose={() => setIsSystemPromptModalOpen(false)}
          locationId={locationId}
          programKey={programKey}
          programName={programName}
          onPromptUpdated={handleSystemPromptUpdated}
        />
      )}
    </>
  );
}; 